/**
 * Security Node Factories - Functions for creating security explorer tree nodes
 *
 * ENGINE AGNOSTIC: These factories are called with engine info to create
 * the appropriate tree structure for MSSQL or PostgreSQL.
 */

import { createConnectionNodeId, createDatabaseNodeId } from '../utils/nodeIdUtils';
import {
  createServerSecurityFolderNodeId,
  createLoginsFolderNodeId,
  createServerRolesFolderNodeId,
  createPgRolesFolderNodeId,
  createLoginRolesFolderNodeId,
  createGroupRolesFolderNodeId,
  createDatabaseSecurityFolderNodeId,
  createUsersFolderNodeId,
  createDatabaseRolesFolderNodeId,
  createSchemasFolderNodeId,
  createLoginNodeId,
  createServerRoleNodeId,
  createDatabaseUserNodeId,
  createDatabaseRoleNodeId,
  createSchemaNodeId,
  createLoginRoleNodeId,
  createGroupRoleNodeId,
  createOracleUserNodeId,
  createOracleRoleNodeId,
  createOracleRolesFolderNodeId,
} from '../utils/securityNodeIdUtils';
import type {
  ServerSecurityFolderNode,
  LoginsFolderNode,
  ServerRolesFolderNode,
  PgRolesFolderNode,
  LoginRolesFolderNode,
  GroupRolesFolderNode,
  DatabaseSecurityFolderNode,
  UsersFolderNode,
  DatabaseRolesFolderNode,
  SchemasFolderNode,
  SecurityFolderNode,
  LoginTreeNode,
  ServerRoleTreeNode,
  DatabaseUserTreeNode,
  DatabaseRoleTreeNode,
  SchemaTreeNode,
  LoginRoleTreeNode,
  GroupRoleTreeNode,
  OracleUserTreeNode,
  OracleRoleTreeNode,
} from './nodeTypes';

// ============================================================================
// MSSQL Server-Level Security Folders
// ============================================================================

/**
 * Creates a server security folder node (MSSQL)
 */
export function createServerSecurityFolderNode(connectionId: string): ServerSecurityFolderNode {
  return {
    id: createServerSecurityFolderNodeId(connectionId),
    parentId: createConnectionNodeId(connectionId),
    name: 'Security',
    type: 'server-security-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
    },
  };
}

/**
 * Creates a logins folder node (MSSQL)
 */
export function createLoginsFolderNode(connectionId: string): LoginsFolderNode {
  return {
    id: createLoginsFolderNodeId(connectionId),
    parentId: createServerSecurityFolderNodeId(connectionId),
    name: 'Logins',
    type: 'logins-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
    },
  };
}

/**
 * Creates a server roles folder node (MSSQL)
 */
export function createServerRolesFolderNode(connectionId: string): ServerRolesFolderNode {
  return {
    id: createServerRolesFolderNodeId(connectionId),
    parentId: createServerSecurityFolderNodeId(connectionId),
    name: 'Server Roles',
    type: 'server-roles-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
    },
  };
}

/**
 * Creates the complete MSSQL server-level security folder structure
 */
export function createMssqlServerSecurityFolders(connectionId: string): SecurityFolderNode[] {
  const securityFolder = createServerSecurityFolderNode(connectionId);
  const loginsFolder = createLoginsFolderNode(connectionId);
  const serverRolesFolder = createServerRolesFolderNode(connectionId);

  securityFolder.children = [loginsFolder, serverRolesFolder];

  return [securityFolder];
}

// ============================================================================
// PostgreSQL Server-Level Security Folders
// ============================================================================

/**
 * Creates a PostgreSQL roles folder node (server-level)
 */
export function createPgRolesFolderNode(connectionId: string): PgRolesFolderNode {
  return {
    id: createPgRolesFolderNodeId(connectionId),
    parentId: createConnectionNodeId(connectionId),
    name: 'Roles',
    type: 'pg-roles-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
    },
  };
}

/**
 * Creates a login roles folder node (PostgreSQL)
 */
export function createLoginRolesFolderNode(connectionId: string): LoginRolesFolderNode {
  return {
    id: createLoginRolesFolderNodeId(connectionId),
    parentId: createPgRolesFolderNodeId(connectionId),
    name: 'Login Roles',
    type: 'login-roles-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
    },
  };
}

/**
 * Creates a group roles folder node (PostgreSQL)
 */
export function createGroupRolesFolderNode(connectionId: string): GroupRolesFolderNode {
  return {
    id: createGroupRolesFolderNodeId(connectionId),
    parentId: createPgRolesFolderNodeId(connectionId),
    name: 'Group Roles',
    type: 'group-roles-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
    },
  };
}

/**
 * Creates the complete PostgreSQL server-level security folder structure
 */
export function createPgsqlServerSecurityFolders(connectionId: string): SecurityFolderNode[] {
  const rolesFolder = createPgRolesFolderNode(connectionId);
  const loginRolesFolder = createLoginRolesFolderNode(connectionId);
  const groupRolesFolder = createGroupRolesFolderNode(connectionId);

  rolesFolder.children = [loginRolesFolder, groupRolesFolder];

  return [rolesFolder];
}

// ============================================================================
// MySQL Server-Level Security Folders
// ============================================================================

/**
 * Creates the complete MySQL server-level security folder structure
 * MySQL has a simple user model: 'user'@'host' accounts under a "Users" folder
 */
export function createMysqlServerSecurityFolders(connectionId: string): SecurityFolderNode[] {
  const securityFolder = createServerSecurityFolderNode(connectionId);
  const usersFolder = createLoginsFolderNode(connectionId);
  // Rename to "Users" for MySQL (reuses logins folder structure)
  usersFolder.name = 'Users';

  securityFolder.children = [usersFolder];

  return [securityFolder];
}

/**
 * Creates the complete MySQL database-level security folder structure
 * MySQL doesn't have database-level security objects (users are server-level)
 */
export function createMysqlDatabaseSecurityFolders(
  _connectionId: string,
  _database: string
): SecurityFolderNode[] {
  return [];
}

// ============================================================================
// Database-Level Security Folders
// ============================================================================

/**
 * Creates a database security folder node
 */
export function createDatabaseSecurityFolderNode(
  connectionId: string,
  database: string
): DatabaseSecurityFolderNode {
  return {
    id: createDatabaseSecurityFolderNodeId(connectionId, database),
    parentId: createDatabaseNodeId(connectionId, database),
    name: 'Security',
    type: 'database-security-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
    },
  };
}

/**
 * Creates a users folder node (MSSQL)
 */
export function createUsersFolderNode(
  connectionId: string,
  database: string
): UsersFolderNode {
  return {
    id: createUsersFolderNodeId(connectionId, database),
    parentId: createDatabaseSecurityFolderNodeId(connectionId, database),
    name: 'Users',
    type: 'users-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
    },
  };
}

/**
 * Creates a database roles folder node
 */
export function createDatabaseRolesFolderNode(
  connectionId: string,
  database: string
): DatabaseRolesFolderNode {
  return {
    id: createDatabaseRolesFolderNodeId(connectionId, database),
    parentId: createDatabaseSecurityFolderNodeId(connectionId, database),
    name: 'Roles',
    type: 'database-roles-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
    },
  };
}

/**
 * Creates a schemas folder node
 */
export function createSchemasFolderNode(
  connectionId: string,
  database: string
): SchemasFolderNode {
  return {
    id: createSchemasFolderNodeId(connectionId, database),
    parentId: createDatabaseSecurityFolderNodeId(connectionId, database),
    name: 'Schemas',
    type: 'schemas-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
      database,
    },
  };
}

/**
 * Creates the complete MSSQL database-level security folder structure
 */
export function createMssqlDatabaseSecurityFolders(
  connectionId: string,
  database: string
): SecurityFolderNode[] {
  const securityFolder = createDatabaseSecurityFolderNode(connectionId, database);
  const usersFolder = createUsersFolderNode(connectionId, database);
  const rolesFolder = createDatabaseRolesFolderNode(connectionId, database);
  const schemasFolder = createSchemasFolderNode(connectionId, database);

  securityFolder.children = [usersFolder, rolesFolder, schemasFolder];

  return [securityFolder];
}

/**
 * Creates the complete PostgreSQL database-level security folder structure
 * Note: PostgreSQL doesn't have database users (roles are server-level)
 */
export function createPgsqlDatabaseSecurityFolders(
  connectionId: string,
  database: string
): SecurityFolderNode[] {
  const securityFolder = createDatabaseSecurityFolderNode(connectionId, database);
  const schemasFolder = createSchemasFolderNode(connectionId, database);

  securityFolder.children = [schemasFolder];

  return [securityFolder];
}

// ============================================================================
// Oracle Server-Level Security Folders
// ============================================================================

/**
 * Creates an Oracle roles folder node (under Security folder)
 */
function createOracleRolesFolderNode(connectionId: string): ServerRolesFolderNode {
  return {
    id: createOracleRolesFolderNodeId(connectionId),
    parentId: createServerSecurityFolderNodeId(connectionId),
    name: 'Roles',
    type: 'server-roles-folder',
    canHaveChildren: true,
    children: [],
    isExpanded: false,
    metadata: {
      connectionId,
    },
  };
}

/**
 * Creates the complete Oracle server-level security folder structure
 * Oracle: Security > Users, Roles
 */
export function createOracleServerSecurityFolders(connectionId: string): SecurityFolderNode[] {
  const securityFolder = createServerSecurityFolderNode(connectionId);
  const usersFolder = createLoginsFolderNode(connectionId);
  usersFolder.name = 'Users';
  const rolesFolder = createOracleRolesFolderNode(connectionId);

  securityFolder.children = [usersFolder, rolesFolder];

  return [securityFolder];
}

/**
 * Creates the complete Oracle database-level security folder structure
 * Oracle doesn't have database-level security objects (schemas are users)
 */
export function createOracleDatabaseSecurityFolders(
  _connectionId: string,
  _database: string
): SecurityFolderNode[] {
  return [];
}

// ============================================================================
// Security Object Nodes
// ============================================================================

/**
 * Creates a login tree node (MSSQL)
 */
export function createLoginNode(
  connectionId: string,
  loginName: string,
  principalId: string,
  isSystem: boolean = false
): LoginTreeNode {
  return {
    id: createLoginNodeId(connectionId, loginName),
    parentId: createLoginsFolderNodeId(connectionId),
    name: loginName,
    type: 'login',
    canHaveChildren: false,
    isExpanded: false,
    metadata: {
      connectionId,
      principalId,
      principalName: loginName,
      engine: 'mssql',
      isSystem,
    },
  };
}

/**
 * Creates a server role tree node (MSSQL)
 */
export function createServerRoleNode(
  connectionId: string,
  roleName: string,
  principalId: string,
  isSystem: boolean = false
): ServerRoleTreeNode {
  return {
    id: createServerRoleNodeId(connectionId, roleName),
    parentId: createServerRolesFolderNodeId(connectionId),
    name: roleName,
    type: 'server-role',
    canHaveChildren: false,
    isExpanded: false,
    metadata: {
      connectionId,
      principalId,
      principalName: roleName,
      engine: 'mssql',
      isSystem,
    },
  };
}

/**
 * Creates a database user tree node (MSSQL)
 */
export function createDatabaseUserNode(
  connectionId: string,
  database: string,
  userName: string,
  principalId: string,
  isSystem: boolean = false
): DatabaseUserTreeNode {
  return {
    id: createDatabaseUserNodeId(connectionId, database, userName),
    parentId: createUsersFolderNodeId(connectionId, database),
    name: userName,
    type: 'database-user',
    canHaveChildren: false,
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      principalId,
      principalName: userName,
      engine: 'mssql',
      isSystem,
    },
  };
}

/**
 * Creates a database role tree node
 */
export function createDatabaseRoleNode(
  connectionId: string,
  database: string,
  roleName: string,
  principalId: string,
  engine: 'mssql' | 'pgsql' | 'mysql' | 'oracle',
  isSystem: boolean = false
): DatabaseRoleTreeNode {
  return {
    id: createDatabaseRoleNodeId(connectionId, database, roleName),
    parentId: createDatabaseRolesFolderNodeId(connectionId, database),
    name: roleName,
    type: 'database-role',
    canHaveChildren: false,
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      principalId,
      principalName: roleName,
      engine,
      isSystem,
    },
  };
}

/**
 * Creates a schema tree node
 */
export function createSchemaNode(
  connectionId: string,
  database: string,
  schemaName: string,
  principalId: string,
  engine: 'mssql' | 'pgsql' | 'mysql' | 'oracle',
  isSystem: boolean = false
): SchemaTreeNode {
  return {
    id: createSchemaNodeId(connectionId, database, schemaName),
    parentId: createSchemasFolderNodeId(connectionId, database),
    name: schemaName,
    type: 'schema',
    canHaveChildren: false,
    isExpanded: false,
    metadata: {
      connectionId,
      database,
      principalId,
      principalName: schemaName,
      engine,
      isSystem,
    },
  };
}

/**
 * Creates a login role tree node (PostgreSQL)
 */
export function createLoginRoleNode(
  connectionId: string,
  roleName: string,
  principalId: string,
  isSystem: boolean = false
): LoginRoleTreeNode {
  return {
    id: createLoginRoleNodeId(connectionId, roleName),
    parentId: createLoginRolesFolderNodeId(connectionId),
    name: roleName,
    type: 'login-role',
    canHaveChildren: false,
    isExpanded: false,
    metadata: {
      connectionId,
      principalId,
      principalName: roleName,
      engine: 'pgsql',
      isSystem,
    },
  };
}

/**
 * Creates a group role tree node (PostgreSQL)
 */
export function createGroupRoleNode(
  connectionId: string,
  roleName: string,
  principalId: string,
  isSystem: boolean = false
): GroupRoleTreeNode {
  return {
    id: createGroupRoleNodeId(connectionId, roleName),
    parentId: createGroupRolesFolderNodeId(connectionId),
    name: roleName,
    type: 'group-role',
    canHaveChildren: false,
    isExpanded: false,
    metadata: {
      connectionId,
      principalId,
      principalName: roleName,
      engine: 'pgsql',
      isSystem,
    },
  };
}

/**
 * Creates an Oracle user tree node
 */
export function createOracleUserNode(
  connectionId: string,
  userName: string,
  isSystem: boolean = false
): OracleUserTreeNode {
  return {
    id: createOracleUserNodeId(connectionId, userName),
    parentId: createLoginsFolderNodeId(connectionId),
    name: userName,
    type: 'oracle-user',
    canHaveChildren: false,
    isExpanded: false,
    metadata: {
      connectionId,
      principalId: userName,
      principalName: userName,
      engine: 'oracle',
      isSystem,
    },
  };
}

/**
 * Creates an Oracle role tree node
 */
export function createOracleRoleNode(
  connectionId: string,
  roleName: string,
  isSystem: boolean = false
): OracleRoleTreeNode {
  return {
    id: createOracleRoleNodeId(connectionId, roleName),
    parentId: createOracleRolesFolderNodeId(connectionId),
    name: roleName,
    type: 'oracle-role',
    canHaveChildren: false,
    isExpanded: false,
    metadata: {
      connectionId,
      principalId: roleName,
      principalName: roleName,
      engine: 'oracle',
      isSystem,
    },
  };
}
